import React, { useMemo, useState } from 'react';
import { message, Tooltip } from 'antd-v5';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Form, Input, Modal, Table } from '@/components/index';
import addIcon from '@/common/images/add.png';
import {
  addCsmBlackListRequest,
  getCsmBlackListListRequest,
  removeCsmBlackListRequest,
} from '@/state/request/blackList';
import './style.less';

const BLACK_LIST_CACHE_KEY = 'PHONE_BLACK_LIST_QUERY_CACHE';
const DEFAULT_QUERY = {
  account: undefined,
};
const FIXED_PAGE_SIZE = 10;

const PhoneBlackListSetting = () => {
  const history = useHistory();
  const [queryForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [totalSize, setTotalSize] = useState(0);

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [relatedModalVisible, setRelatedModalVisible] = useState(false);
  const [relatedStallList, setRelatedStallList] = useState([]);
  const [relatedCurrentRecord, setRelatedCurrentRecord] = useState(null);

  const saveQueryCache = (nextQuery, nextPageNo) => {
    window.sessionStorage.setItem(
      BLACK_LIST_CACHE_KEY,
      JSON.stringify({
        query: nextQuery || DEFAULT_QUERY,
        pageNo: nextPageNo,
      }),
    );
  };

  const fetchList = async (params = DEFAULT_QUERY, currentPageNo = pageNo) => {
    setLoading(true);
    try {
      const res = await getCsmBlackListListRequest({
        ...params,
        pageNo: currentPageNo,
        pageSize: FIXED_PAGE_SIZE,
      });
      if (res?.code === 200) {
        const {
          pageList = [],
          totalSize: nextTotalSize = 0,
          pageNo: nextPageNo = currentPageNo,
        } = res?.data || {};
        setDataSource(pageList);
        setTotalSize(nextTotalSize);
        setPageNo(nextPageNo);
        saveQueryCache(params, nextPageNo);
      } else {
        setDataSource([]);
        setTotalSize(0);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const cache = window.sessionStorage.getItem(BLACK_LIST_CACHE_KEY);
    if (cache) {
      try {
        const {
          query = DEFAULT_QUERY,
          pageNo: cachePageNo = 1,
        } = JSON.parse(cache) || {};
        queryForm.setFieldsValue(query);
        setPageNo(cachePageNo);
        fetchList(query, cachePageNo);
        return;
      } catch (e) {
        // 解析失败时回退默认查询
      }
    }
    saveQueryCache(DEFAULT_QUERY, 1);
    fetchList(DEFAULT_QUERY, 1);
  }, []);

  const handleSearch = (values) => {
    setPageNo(1);
    saveQueryCache(values, 1);
    fetchList(values, 1);
  };

  const handleReset = () => {
    queryForm.resetFields();
    setPageNo(1);
    saveQueryCache(DEFAULT_QUERY, 1);
    fetchList(DEFAULT_QUERY, 1);
  };

  const handleOpenAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const handleCloseAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(false);
  };

  const handleSubmitAdd = async () => {
    try {
      const values = await addForm.validateFields();
      const res = await addCsmBlackListRequest(values);
      if (res?.code === 200) {
        message.success('添加黑名单成功');
        handleCloseAddModal();
        setPageNo(1);
        const currentQuery = queryForm.getFieldsValue();
        saveQueryCache(currentQuery, 1);
        fetchList(currentQuery, 1);
      }
    } catch (e) {
      // 表单校验失败不提示
    }
  };

  const handleRemove = async (record) => {
    const res = await removeCsmBlackListRequest({ id: record.id });
    if (res?.code === 200) {
      message.success('移除成功');
      const currentQuery = queryForm.getFieldsValue();
      saveQueryCache(currentQuery, pageNo);
      fetchList(currentQuery, pageNo);
    }
  };

  const handleOpenRelatedModal = (record) => {
    setRelatedCurrentRecord(record);
    setRelatedModalVisible(true);
    setRelatedStallList(record?.stallList || []);
  };

  const handleCloseRelatedModal = () => {
    setRelatedModalVisible(false);
    setRelatedStallList([]);
    setRelatedCurrentRecord(null);
  };

  const relatedColumns = useMemo(
    () => [
      {
        title: '商行业务ID',
        dataIndex: 'businessCode',
        key: 'businessCode',
        width: 180,
        render: (text, record) => (
          <span
            className='blacklist-link'
            onClick={() => {
              const stallId = record.stallId || record.id;
              if (!stallId) {
                message.warning('未找到商行ID，无法跳转详情');
                return;
              }
              history.push({
                pathname: '/booth/stallManager/stallManagementDetail',
                state: { stallId, from: 'GO_BACK' },
              });
            }}
          >
            {text || '--'}
          </span>
        ),
      },
      {
        title: '所在商行',
        dataIndex: 'stallName',
        key: 'stallName',
      },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: '手机号或openId',
        dataIndex: 'account',
        key: 'account',
        width: 160,
      },
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: (text) => (text ? text : '--'),
      },
      {
        title: '关联商行数',
        dataIndex: 'stallList',
        key: 'stallList',
        width: 140,
        render: (text = [], record) =>
          text.length > 0 ? (
            <span className='blacklist-link' onClick={() => handleOpenRelatedModal(record)}>
              {text.length}
            </span>
          ) : (
            '--'
          ),
      },
      {
        title: '添加原因',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
        width: 200,
        render: (text) => (
          <Tooltip title={text} placement='topLeft' autoAdjustOverflow={false}>
            <span className='text-ellipsis-2'>{text || '--'}</span>
          </Tooltip>
        ),
      },
      {
        title: '操作时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      {
        title: '操作人',
        dataIndex: 'personName',
        key: 'personName',
        width: 120,
      },
      {
        title: '操作',
        key: 'action',
        width: 80,
        render: (_, record) => (
          <span
            className='blacklist-remove'
            onClick={() => {
              modal.confirm({
                className: 'ui-modal',
                title: '确认移除黑名单？',
                icon: null,
                okText: '确认移除',
                cancelText: '取消',
                okType: 'danger',
                content: (
                  <span>
                    移除后，该手机号或openId关联的所有账号将被解除拉黑状态，相关用户可正常登录和使用系统。
                    <br />
                    <br />
                    确认要将 <span style={{ fontWeight: 'bold' }}>{record.name || ''}</span>
                    <span style={{ fontWeight: 'bold' }}>({record.account || ''})</span>{' '}
                    从黑名单中移除吗？
                  </span>
                ),
                onOk: () => handleRemove(record),
              });
            }}
          >
            移除
          </span>
        ),
      },
    ],
    [],
  );

  const handleTableChange = (pagination) => {
    const nextPageNo = pagination?.current || 1;
    const currentQuery = queryForm.getFieldsValue();
    setPageNo(nextPageNo);
    saveQueryCache(currentQuery, nextPageNo);
    fetchList(currentQuery, nextPageNo);
  };

  const tablePagination = useMemo(
    () => ({
      current: pageNo,
      pageSize: FIXED_PAGE_SIZE,
      total: totalSize,
      showSizeChanger: false,
    }),
    [pageNo, totalSize],
  );

  return (
    <PageHeaderLayout>
      <div className='phone-blacklist-setting-container'>
        <Card bordered={false} bodyStyle={{ padding: '16px 24px' }}>
          <Form
            form={queryForm}
            layout='inline'
            onFinish={handleSearch}
            initialValues={DEFAULT_QUERY}
            style={{ marginBottom: 16 }}
          >
            <Form.Item label='手机号或openId' name='account'>
              <Input style={{ width: 240 }} allowClear placeholder='请输入手机号或openId' maxLength={50} />
            </Form.Item>
            <Form.Item>
              <Button type='primary' htmlType='submit'>
                查询
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={handleReset}>重置</Button>
            </Form.Item>
            <Form.Item style={{ marginLeft: 'auto', marginRight: 0 }}>
              <Button type='primary' onClick={handleOpenAddModal}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={addIcon} style={{ width: 16, height: 16, marginRight: 4 }} alt='add' />
                  添加黑名单
                </div>
              </Button>
            </Form.Item>
          </Form>

          <Table
            rowKey='id'
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            pagination={tablePagination}
            onChange={handleTableChange}
            scroll={{ x: 'scroll' }}
          />
        </Card>

        <Modal
          className='ui-modal'
          title='添加黑名单'
          open={addModalVisible}
          onCancel={handleCloseAddModal}
          footer={
            <div>
              <Button onClick={handleCloseAddModal}>取消</Button>
              <Button type='primary' onClick={handleSubmitAdd}>
                确认添加
              </Button>
            </div>
          }
          destroyOnClose
          width={520}
          maskClosable={false}
        >
          <Form
            form={addForm}
            style={{ padding: '12px 0', marginBottom: -16 }}
            labelCol={{ flex: '140px' }}
            wrapperCol={{ flex: '332px' }}
            colon={false}
          >
            <Form.Item
              name='account'
              label='手机号或openId'
              rules={[{ max: 50, required: true, message: '请输入手机号或openId，最多50个字符' }]}
            >
              <Input placeholder='请输入手机号或openId' allowClear />
            </Form.Item>
            <Form.Item
              name='remark'
              label='添加原因'
              rules={[{ max: 200, message: '添加原因最多200字' }]}
            >
              <Input.TextArea
                placeholder='请输入添加原因'
                autoSize={{ minRows: 4 }}
                showCount
                maxLength={200}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          className='ui-modal'
          title='关联商行'
          open={relatedModalVisible}
          onCancel={handleCloseRelatedModal}
          footer={null}
          width={620}
          destroyOnClose
        >
          <Table
            rowKey={(record) => record.stallId || record.id || record.businessCode}
            columns={relatedColumns}
            dataSource={relatedStallList}
            pagination={false}
          />
        </Modal>
      </div>
      {contextHolder}
    </PageHeaderLayout>
  );
};

export default PhoneBlackListSetting;
